import React, { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";

import KioskModal from "../modals/KioskModal";

// Эмодзи из подписей убраны: в новом дизайне тон задают иконка и цвет плашки,
// а смайлы на киоск-экране выглядели инородно.
const ERROR_LABELS = {
    ru: {
        "paper is out (a4)":     "Закончилась бумага A4",
        "paper jam (main unit)": "Замятие бумаги",
        "output tray is full":   "Выходной лоток переполнен",
    },
    en: {
        "paper is out (a4)":     "Paper A4 is out",
        "paper jam (main unit)": "Paper jam detected",
        "output tray is full":   "Output tray is full",
    },
    kk: {
        "paper is out (a4)":     "A4 қағазы таусылды",
        "paper jam (main unit)": "Қағаз кептелісі",
        "output tray is full":   "Шығыс науасы толып кетті",
    },
};

const translations = {
    ru: {
        title:        "Принтер временно вышел из строя",
        subtitle:     "Что-то пошло не так, но мы уже в курсе",
        errors_label: "Обнаруженные проблемы",
        support:      "Пожалуйста, обратитесь к персоналу или позвоните в поддержку — мы починим как можно быстрее",
    },
    en: {
        title:        "Printer is temporarily out of service",
        subtitle:     "Something went wrong, but we're on it",
        errors_label: "Detected issues",
        support:      "Please contact staff or call support — we'll fix it as soon as possible",
    },
    kk: {
        title:        "Принтер уақытша істен шықты",
        subtitle:     "Бірдеңе дұрыс болмады, бірақ біз хабардармыз",
        errors_label: "Анықталған мәселелер",
        support:      "Қызметкерге хабарласыңыз немесе қолдау қызметіне қоңырау шалыңыз — тез арада жөндейміз",
    },
};

const LANGUAGES = ["ru", "en", "kk"];

function PrinterErrorBanner() {
    const [errors, setErrors]       = useState([]);
    const [currentLang, setCurrentLang] = useState("ru");

    // Слушаем события принтера от Electron
    useEffect(() => {
        const cleanup = window.electronAPI?.onPrinterError((event, data) => {
            setErrors(data.active ? (data.errors ?? []) : []);
        });
        return () => cleanup?.();
    }, []);

    // Авто-смена языка пока показан баннер
    useEffect(() => {
        if (errors.length === 0) return;
        const interval = setInterval(() => {
            setCurrentLang((prev) => {
                const idx = LANGUAGES.indexOf(prev);
                return LANGUAGES[(idx + 1) % LANGUAGES.length];
            });
        }, 8000);
        return () => clearInterval(interval);
    }, [errors.length]);

    const isVisible = errors.length > 0;
    const tr = translations[currentLang];
    const labels = ERROR_LABELS[currentLang];

    return (
        <AnimatePresence>
            {isVisible && (
                <KioskModal
                    tone="danger"
                    zClass="z-[9999]"
                    title={tr.title}
                    description={tr.subtitle}
                    titleKey={currentLang}
                    descriptionKey={currentLang}
                    icon={
                        <svg
                            className="w-[5.4vh] h-[5.4vh]"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                        >
                            <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3m-3 11H8v-5h8zm3-7c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1m-1-9H6v4h12z" />
                            <path d="m13.41 13l1.59-1.59L13.59 10L12 11.59L10.41 10L9 11.41L10.59 13L9 14.59L10.41 16L12 14.41L13.59 16L15 14.59z" />
                        </svg>
                    }
                    footer={
                        <>
                            <div className="bg-[#F7E1DC] rounded-[2.2vh] px-[2.4vw] py-[1.6vh] text-left">
                                <p className="text-[1.3vh] font-bold uppercase tracking-[0.15em] text-[#C2543E]">
                                    {tr.errors_label}
                                </p>
                                {errors.map((key) => (
                                    <p
                                        key={key}
                                        className="text-[1.8vh] font-bold mt-[0.8vh]"
                                    >
                                        {labels[key] ?? key}
                                    </p>
                                ))}
                            </div>

                            <p className="text-[1.6vh] font-semibold text-[#7B8290] leading-snug mt-[1.8vh]">
                                {tr.support}
                            </p>

                            {/* Индикатор того, на каком из трёх языков сейчас текст */}
                            <div className="flex justify-center gap-[0.6vh] mt-[1.8vh]">
                                {LANGUAGES.map((lang) => (
                                    <div
                                        key={lang}
                                        className={`h-[0.9vh] rounded-full transition-all duration-500 ${
                                            lang === currentLang
                                                ? "w-[2.4vh] bg-[#C2543E]"
                                                : "w-[0.9vh] bg-[#E1E4EB]"
                                        }`}
                                    />
                                ))}
                            </div>
                        </>
                    }
                />
            )}
        </AnimatePresence>
    );
}

export default PrinterErrorBanner;
